import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    memberToReferral,
    memberToStaff,
    teamDtoToTeam,
} from './team.converters';
import { MemberRole } from './team.types';
import {
    TeamsService,
    UsersService,
    CreateTeamReqDto,
    ListItemAction,
    PersonNameData,
    type GetTeamsResDto,
    type PhoneNumberData,
    type GetTeamsResItemDto,
    type UpdateMembersTeamsResDto,
    type GetTeamResDto,
    CreateTeamResDto,
    UpdateUserResDto,
    UpdateUserRes1Dto,
} from '../../generated';
import { CustomMutationOptions, CustomQueryOptions } from '../common.types';

const useDebouncedStaffKey = 'useDebouncedStaffKey';
const useDebouncedReferralKey = 'useDebouncedReferralKey';
const useTeamsKey = 'useTeamsKey';
export const useTeamKey = 'useTeamKey';
const useUserKey = 'useUserKey';

interface Filter {
    productId?: string;
    email?: string;
    name?: string;
}

export const useTeams = (options: CustomQueryOptions<GetTeamsResDto> = {}) => {
    const { data, ...other } = useQuery({
        queryFn: async () => {
            const response = await TeamsService.getTeams({});

            if (!response.data) throw new Error('Failed');
            return response.data;
        },
        queryKey: [useTeamsKey],
        ...options,
    });

    return {
        data: data?.items?.map(teamDtoToTeam),
        ...other,
    };
};

export const useTeam = (
    teamId?: string,
    options: CustomQueryOptions<GetTeamResDto> = {}
) => {
    const { data: teamDto, ...other } = useQuery({
        queryFn: async () => {
            const response = await TeamsService.getTeam({
                path: { team_id: teamId as string },
            });

            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        queryKey: [useTeamKey, teamId],
        enabled: !!teamId,
        ...options,
    });

    return {
        data: useMemo(
            () => (teamDto ? teamDtoToTeam(teamDto) : undefined),
            [teamDto]
        ),
        ...other,
    };
};

const mergeItems = (
    item: GetTeamsResItemDto,
    newItem: Partial<UpdateMembersTeamsResDto>
): GetTeamsResItemDto =>
    ({
        ...item,
        ...newItem,
    } as GetTeamsResItemDto);

export const useTeamCache = () => {
    const queryClient = useQueryClient();

    const update = (
        id: string,
        newItem: Omit<UpdateMembersTeamsResDto, 'id' | 'created'>
    ) => {
        queryClient
            .getQueriesData({
                queryKey: [useTeamsKey],
                exact: false,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<GetTeamsResDto>(queryKey, data =>
                    !data?.items
                        ? undefined
                        : {
                              ...data,
                              items: data.items.map(item =>
                                  item.id === id
                                      ? mergeItems(item, newItem)
                                      : item
                              ),
                          }
                );
            });
        queryClient
            .getQueriesData({
                queryKey: [useTeamKey, id],
                exact: true,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<GetTeamResDto>(queryKey, data => {
                    return data
                        ? (mergeItems(data, newItem) as GetTeamResDto)
                        : undefined;
                });
            });
        queryClient
            .getQueriesData({
                queryKey: [useDebouncedStaffKey],
                exact: false,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<GetTeamsResDto>(queryKey, data =>
                    !data?.items
                        ? undefined
                        : {
                              ...data,
                              items: data.items.map(item =>
                                  item.id === id
                                      ? mergeItems(item, newItem)
                                      : item
                              ),
                          }
                );
            });
        queryClient
            .getQueriesData({
                queryKey: [useDebouncedReferralKey],
                exact: false,
            })
            .forEach(([queryKey]) => {
                queryClient.setQueryData<GetTeamsResDto>(queryKey, data =>
                    !data?.items
                        ? undefined
                        : {
                              ...data,
                              items: data.items.map(item =>
                                  item.id === id
                                      ? mergeItems(item, newItem)
                                      : item
                              ),
                          }
                );
            });
    };

    return {
        update,
    };
};

export const useCreateTeam = (
    options: CustomMutationOptions<CreateTeamReqDto, CreateTeamResDto> = {}
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async data => {
            const response = await TeamsService.createTeam({
                body: data,
            });

            if (!response.data) throw new Error('Failed to invite member');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.invalidateQueries([useUserKey]);
            if (options?.onSuccess) options.onSuccess(...args);
        },
    });
};

export const useInviteTeamMember = (
    options: CustomMutationOptions<
        {
            teamId: string;
            membership: MemberRole;
            email: string;
            company?: string;
            noEmail: boolean;
        },
        UpdateMembersTeamsResDto
    > = {}
) => {
    const { update } = useTeamCache();

    return useMutation({
        mutationFn: async ({ teamId, membership, email, company, noEmail }) => {
            const response = await TeamsService.updateMembersTeams({
                path: { team_id: teamId },
                body: {
                    items: [
                        {
                            reference: {
                                contact_details: {
                                    emails: [email],
                                },
                                organization: company
                                    ? {
                                          name: company,
                                      }
                                    : undefined,
                            },
                            membership: {
                                role: { type: membership },
                                action: ListItemAction.ADD,
                            },
                            action: ListItemAction.ADD,
                        },
                    ],
                },
                query: { no_email: noEmail },
            });

            if (!response.data) throw new Error('Failed to invite member');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].teamId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useSendInviteTeamMember = (
    options: CustomMutationOptions<
        {
            teamId: string;
            memberId: string;
        },
        UpdateMembersTeamsResDto
    > = {}
) => {
    const { update } = useTeamCache();

    return useMutation({
        mutationFn: async ({ teamId, memberId }) => {
            const response = await TeamsService.inviteMemberTeams({
                path: {
                    team_id: teamId,
                    member_id: memberId,
                },
            });

            if (!response.data) throw new Error('Failed');

            return response.data as UpdateMembersTeamsResDto;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].teamId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useAcceptTeamInvitation = (
    options: CustomMutationOptions<
        {
            key: string;
        },
        UpdateUserResDto | UpdateUserRes1Dto
    > = {}
) => {
    return useMutation({
        mutationFn: async ({ key }) => {
            const response = await UsersService.acceptInvitationUsers({
                path: { key },
            });

            if (!response.data) throw new Error('Failed to accept invitation');

            return response.data;
        },
        ...options,
    });
};

export const useRemoveTeamMember = (
    options: CustomMutationOptions<
        {
            teamId: string;
            id: string;
        },
        UpdateMembersTeamsResDto
    > = {}
) => {
    const { update } = useTeamCache();

    return useMutation({
        mutationFn: async ({ teamId, id }) => {
            const response = await TeamsService.updateMembersTeams({
                path: { team_id: teamId },
                body: {
                    items: [
                        {
                            id,
                            action: ListItemAction.REMOVE,
                        },
                    ],
                },
            });
            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].teamId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateTeamMember = (
    options: CustomMutationOptions<
        {
            teamId: string;
            id: string;
            name?: PersonNameData;
            phoneNumbers: PhoneNumberData[];
            company?: string;
        },
        UpdateMembersTeamsResDto
    > = {}
) => {
    const { update } = useTeamCache();

    return useMutation({
        mutationFn: async ({ teamId, id, name, phoneNumbers, company }) => {
            const response = await TeamsService.updateMembersTeams({
                path: { team_id: teamId },
                body: {
                    items: [
                        {
                            id,
                            reference: {
                                contact_details: {
                                    phone_numbers: phoneNumbers,
                                },
                                name,
                                organization:
                                    company !== undefined
                                        ? {
                                              name: company,
                                          }
                                        : undefined,
                            },
                            action: ListItemAction.UPDATE,
                        },
                    ],
                },
            });
            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) update(args[1].teamId, args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useDebouncedStaff = (
    search: string,
    productId: string,
    debounce: number = 1000
) => {
    const [debouncedParams, setDebouncedParams] = React.useState<
        Filter | undefined
    >();

    React.useEffect(() => {
        const handler = setTimeout(() => {
            const filter: Filter = {};

            if (search.match(/^[^@]+@[^@.]+\.[a-zA-Z]+$/))
                filter.email = search;
            else if (search.length > 2) {
                filter.name = search;
            }
            setDebouncedParams(
                Object.keys(filter).length
                    ? { productId, ...filter }
                    : undefined
            );
        }, debounce);

        return () => {
            clearTimeout(handler);
        };
    }, [search, debounce]);

    const { data, ...other } = useQuery({
        queryFn: async () => {
            const params = debouncedParams as Filter;

            const response = await TeamsService.getTeams1({
                body: {
                    product_id: params.productId as string,
                    email: params.email,
                    name: params.name,
                },
            });

            return response.data;
        },
        queryKey: [useDebouncedStaffKey, debouncedParams],
        keepPreviousData: true,
        staleTime: debounce,
        enabled: !!debouncedParams,
    });

    const parsedData = useMemo(
        () =>
            debouncedParams && data?.items
                ? data.items.map(memberToStaff)
                : undefined,
        [data]
    );

    return {
        data: parsedData,
        ...other,
        isLoading: other.isFetching,
    };
};

interface ReferralFilter {
    email?: string;
    name?: string;
    productId?: string;
}

export const useDebouncedReferral = (
    search: string,
    productId?: string,
    debounce: number = 1000
) => {
    const [debouncedParams, setDebouncedParams] = React.useState<
        ReferralFilter | undefined
    >();

    React.useEffect(() => {
        const handler = setTimeout(() => {
            const filter: ReferralFilter = {};

            if (search.match(/^[^@]+@[^@.]+\.[a-zA-Z]+$/))
                filter.email = search;
            else if (search.length > 2) {
                filter.name = search;
            }
            setDebouncedParams(
                Object.keys(filter).length
                    ? { productId, ...filter }
                    : undefined
            );
        }, debounce);

        return () => {
            clearTimeout(handler);
        };
    }, [search, debounce]);

    const { data, ...other } = useQuery({
        queryFn: async () => {
            const params = debouncedParams as ReferralFilter;

            const response = await TeamsService.getTeams1({
                body: {
                    product_id: params.productId as string,
                    email: params.email,
                    name: params.name,
                },
            });

            return response.data;
        },
        queryKey: [useDebouncedReferralKey, debouncedParams],
        keepPreviousData: true,
        staleTime: debounce,
        enabled: !!debouncedParams,
    });

    const parsedData = useMemo(
        () =>
            debouncedParams && data?.items
                ? data.items.map(memberToReferral)
                : undefined,
        [data]
    );

    return {
        data: parsedData,
        ...other,
        isLoading: other.isFetching,
    };
};

export const useRemoveTeam = (
    options?: CustomMutationOptions<{ teamId: string; reason?: string }, null>
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ teamId, reason }) => {
            await TeamsService.deleteTeam({
                path: { team_id: teamId },
                query: reason ? { reason } : {},
            });

            return null;
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.invalidateQueries([useUserKey]);
            if (options?.onSuccess) options.onSuccess(...args);
        },
    });
};
